import { Box, Button, FormControl } from '@mui/material';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { spaces } from '../../assets/styles/themes/tokens';
import LabelledTextField from '../../components/LabelledTextField/LabelledTextField';
import FooterLegalNavigation from '../../components/login/FooterLegalNavigation';
import PageContainer from '../../components/login/PageContainer';
import OnboardingHeader from '../../components/onboarding/OnboardingHeader';
import { errorCodes, routes } from '../../constants';
import services from '../../services';
import appContext from '../../services/appContext.service';
import { trackingEvents } from '../../services/tracking/trackConfig';
import useRouter from '../../utils/useRouter';
import useScreenSize from '../../utils/useScreenSize';

export interface OnboardingSteps {
	password: string;
	roles: string;
	schule: string;
	schulstufe: string;
	schulfaecher: string;
	wichtigkeit: string;
	bundesland: string;
}

interface ResetPasswordPayload {
	email: string;
}

const resetPasswordFormValidator = yup.object<ResetPasswordPayload>({
	email: yup.string().email('The email is not valid').required('This field is required'),
});

const NewResetPasswordPage: React.FC = () => {
	const { t } = useTranslation();
	const {
		router: { push },
	} = useRouter();
	const { register, getValues, errors, formState } = useForm<ResetPasswordPayload>({
		mode: 'onBlur',
		validationSchema: resetPasswordFormValidator,
	});
	const { isXsScreen } = useScreenSize();

	const handleResetPassword = async () => {
		const value = getValues();
		try {
			await services.user.initPasswordReset(value.email);
			appContext.setResetPasswordEmail(value.email);
			push(`/${routes.resetPasswordConfirmed.root}`);
			services.track.eventTrack(trackingEvents.passwordResetSuccessful);
		} catch (error: any) {
			const errorCode = error.data?.code;
			const statusCode = error.statusCode;
			if (errorCode === errorCodes.ipRestriction) {
				push(`/${routes.ipRangeError.root}`);
			}
			const payload = {
				other: {
					status_code: statusCode,
				},
			};
			services.track.eventTrack(trackingEvents.passwordResetFailure, payload);
		}
	};

	return (
		<PageContainer pageBodySx={{ gap: 0 }}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: isXsScreen ? spaces.xl : spaces.xxxl,
				}}
			>
				<OnboardingHeader
					title={t('You can not log in?')}
					subtitle={t(
						'Enter the email address you used to register with myLessons to create a new password.'
					)}
				/>
				<Box
					sx={{
						height: 'fit-content',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							height: '100%',
							gap: spaces.md,
						}}
					>
						<FormControl fullWidth sx={{ gap: spaces.md }}>
							<LabelledTextField
								dataTestId="reset-password-email-input"
								name="email"
								label={t('E-Mail-Address *')}
								placeholder={t('eg max.mustermann@beispiel.de')}
								inputRef={register}
								error={errors.email}
							/>
						</FormControl>
						<Button
							variant="contained"
							size="large"
							color="primary"
							onClick={handleResetPassword}
							data-testid="send-link-per-email-button"
							disabled={formState.isSubmitting}
						>
							{t('Send E-Mail')}
						</Button>
					</Box>
				</Box>
			</Box>
			<Button
				variant="text"
				size="medium"
				color="secondary"
				onClick={() => {
					push(`/${routes.login.root}`);
				}}
				data-testid="back-to-login-button"
				sx={{
					marginTop: 'auto',
				}}
			>
				{t('Back to login')}
			</Button>
			<FooterLegalNavigation isSmallScreen={isXsScreen} />
		</PageContainer>
	);
};

export default NewResetPasswordPage;
